import { z } from 'zod'

// Parcours officiel d'une route (module Routes de RECA App V3) et son instantané dans une Mission terrain
// (migration 20261118010000_route_paths_v1). Le bureau calcule, vérifie et valide le parcours; la tablette le reçoit
// figé au démarrage et ne le recalcule jamais. Les liaisons (dépôt → route, route → route, route → dépôt) dépendent
// de la position réelle de la tablette : le serveur en donne les extrémités, la tablette en demande le tracé au
// moment de s'en servir. Documentation : reca-operator-v3/docs/integration-parcours-reca-app-v3.md.

const timestampSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'Horodatage invalide')
const uuidSchema = z.uuid()

/** Au plus d'arrêts (résidences + points de passage) par appel au fournisseur (limite Mapbox Directions). */
export const ROUTE_PATH_PROVIDER_MAX_STOPS = 25 as const
/** Au plus de points de passage par route. */
export const ROUTE_PATH_MAX_WAYPOINTS = 100 as const
/** Au plus de sommets de géométrie par parcours (taille de l'instantané). */
export const ROUTE_PATH_MAX_VERTICES = 50_000 as const
/**
 * Durée de conservation d'un parcours calculé. Les conditions de conservation des itinéraires du fournisseur ne sont
 * pas confirmées : 30 jours par prudence, puis recalcul obligatoire.
 */
export const ROUTE_PATH_RETENTION_DAYS = 30 as const

export const routePathProviderSchema = z.enum(['MAPBOX_DIRECTIONS'])
export type RoutePathProvider = z.infer<typeof routePathProviderSchema>

/** Profil du fournisseur : un profil automobile, jamais présenté comme adapté au tracteur. */
export const routePathProfileSchema = z.enum(['driving'])
export type RoutePathProfile = z.infer<typeof routePathProfileSchema>

/** `[latitude, longitude]` : forme compacte, l'instantané d'une route compte des milliers de sommets. */
export const latLngTupleSchema = z.tuple([z.number().min(-90).max(90), z.number().min(-180).max(180)])
export type LatLngTuple = z.infer<typeof latLngTupleSchema>

/**
 * Résidence à servir, dans l'ordre de la route. `point` est le point routé : l'entrée (drapeau) si elle existe,
 * sinon la position d'intervention (`effective_latitude/longitude`). `service` reste la position d'intervention.
 */
export const routePathStopSchema = z.object({
  serviceLocationId: uuidSchema,
  order: z.number().int().positive(),
  point: latLngTupleSchema,
  pointSource: z.enum(['FLAG', 'SERVICE']),
  service: latLngTupleSchema,
})
export type RoutePathStop = z.infer<typeof routePathStopSchema>

/** Point de passage imposé par le bureau entre deux résidences; jamais une résidence à servir. */
export const routePathWaypointSchema = z.object({
  id: uuidSchema,
  /** Résidence après laquelle le point est franchi (jamais la dernière : aucune étape ne la suit). */
  afterServiceLocationId: uuidSchema,
  /** Rang parmi les points du même intervalle (0, 1, …). */
  rank: z.number().int().nonnegative(),
  point: latLngTupleSchema,
})
export type RoutePathWaypoint = z.infer<typeof routePathWaypointSchema>

/** Étape routée d'une résidence à la suivante, points de passage compris. */
export const routePathLegSchema = z.object({
  fromServiceLocationId: uuidSchema,
  toServiceLocationId: uuidSchema,
  distanceMeters: z.number().nonnegative(),
  /** Durée du profil automobile du fournisseur, pas celle d'un tracteur. */
  durationSeconds: z.number().nonnegative(),
  geometry: z.array(latLngTupleSchema).min(2),
})
export type RoutePathLeg = z.infer<typeof routePathLegSchema>

export const routePathStatusSchema = z.enum(['CALCULATED', 'VALIDATED', 'SUPERSEDED'])
export type RoutePathStatus = z.infer<typeof routePathStatusSchema>

export const routePathSchema = z.object({
  id: uuidSchema,
  routeId: uuidSchema,
  version: z.number().int().positive(),
  status: routePathStatusSchema,
  provider: routePathProviderSchema,
  profile: routePathProfileSchema,
  /** Empreinte de la composition routée (résidences, ordre, points routés) au calcul. */
  fingerprint: z.string().min(1),
  stops: z.array(routePathStopSchema).min(2),
  waypoints: z.array(routePathWaypointSchema),
  legs: z.array(routePathLegSchema).min(1),
  distanceMeters: z.number().nonnegative(),
  durationSeconds: z.number().nonnegative(),
  calculatedAt: timestampSchema,
  calculatedByName: z.string().nullable(),
  validatedAt: timestampSchema.nullable(),
  validatedByName: z.string().nullable(),
  expiresAt: timestampSchema,
})
export type RoutePath = z.infer<typeof routePathSchema>

/**
 * État présenté dans l'éditeur. Calculé par le serveur à la lecture, jamais stocké :
 * - `NOT_CALCULATED` : aucun parcours pour la composition actuelle ni aucun validé;
 * - `TO_VALIDATE` : un calcul enregistré correspond à la composition actuelle, pas encore validé;
 * - `VALIDATED` : le parcours validé correspond à la composition actuelle et n'est pas expiré;
 * - `TO_RECALCULATE` : la composition a changé (ajout, retrait, ordre, entrée déplacée) ou le parcours a expiré.
 */
export const routePathStateSchema = z.enum(['NOT_CALCULATED', 'TO_VALIDATE', 'VALIDATED', 'TO_RECALCULATE'])
export type RoutePathState = z.infer<typeof routePathStateSchema>

export const routePathViewSchema = z.object({
  routeId: uuidSchema,
  routeVersion: z.number().int().nonnegative(),
  state: routePathStateSchema,
  /** Empreinte de la composition actuelle de la route. */
  fingerprint: z.string().min(1),
  /** Résidences routables actuelles, dans l'ordre (sans les adresses à localiser). */
  stops: z.array(routePathStopSchema),
  /** Résidences de la route sans coordonnées : exclues du calcul et signalées. */
  missingServiceLocationIds: z.array(uuidSchema),
  /** Dernier calcul enregistré non validé (peut être périmé : comparer son empreinte). */
  draft: routePathSchema.nullable(),
  /** Dernier parcours validé, même périmé : identifiable sans passer pour la nouvelle version. */
  validated: routePathSchema.nullable(),
})
export type RoutePathView = z.infer<typeof routePathViewSchema>

export const saveRoutePathCommandSchema = z.object({
  idempotencyKey: uuidSchema,
  routeId: uuidSchema,
  /** Version de la route lue par l'éditeur : refus `ROUTE_PATH_ROUTE_CHANGED` si elle a bougé. */
  expectedRouteVersion: z.number().int().nonnegative(),
  provider: routePathProviderSchema,
  profile: routePathProfileSchema,
  stops: z.array(routePathStopSchema).min(2),
  waypoints: z.array(routePathWaypointSchema).max(ROUTE_PATH_MAX_WAYPOINTS),
  legs: z.array(routePathLegSchema).min(1),
})
export type SaveRoutePathCommand = z.infer<typeof saveRoutePathCommandSchema>

export const validateRoutePathCommandSchema = z.object({
  idempotencyKey: uuidSchema,
  pathId: uuidSchema,
})
export type ValidateRoutePathCommand = z.infer<typeof validateRoutePathCommandSchema>

export const routePathCommandResultSchema = z.object({
  pathId: uuidSchema,
  version: z.number().int().positive(),
  status: routePathStatusSchema,
  replayed: z.boolean(),
})
export type RoutePathCommandResult = z.infer<typeof routePathCommandResultSchema>

// ------------------------------------------------------------------ Instantané dans la Mission (tablette)

export const depotSchema = z.object({
  label: z.string(),
  point: latLngTupleSchema,
})
export type Depot = z.infer<typeof depotSchema>

/** Parcours validé figé au démarrage de la Mission; jamais remplacé pendant la Mission. */
export const missionRoutePathSchema = z.object({
  pathId: uuidSchema,
  version: z.number().int().positive(),
  provider: routePathProviderSchema,
  profile: routePathProfileSchema,
  validatedAt: timestampSchema,
  expiresAt: timestampSchema,
  stops: z.array(routePathStopSchema).min(2),
  legs: z.array(routePathLegSchema).min(1),
  distanceMeters: z.number().nonnegative(),
  durationSeconds: z.number().nonnegative(),
})
export type MissionRoutePath = z.infer<typeof missionRoutePathSchema>

/**
 * Pourquoi la Mission n'a pas de parcours : jamais validé, composition changée depuis la validation, ou expiré.
 * `READY` : `path` est présent.
 */
export const missionRouteGuideStatusSchema = z.enum(['READY', 'NOT_CALCULATED', 'STALE', 'EXPIRED'])
export type MissionRouteGuideStatus = z.infer<typeof missionRouteGuideStatusSchema>

/** Route suivante de l'opérateur (ordre du bureau) : extrémité de la liaison route → route. */
export const missionNextRouteSchema = z.object({
  routeId: uuidSchema,
  name: z.string(),
  color: z.string().nullable(),
  /** Première résidence routable de la route suivante; `null` : aucune coordonnée connue. */
  firstStop: latLngTupleSchema.nullable(),
})
export type MissionNextRoute = z.infer<typeof missionNextRouteSchema>

export const missionRouteGuideSchema = z.object({
  status: missionRouteGuideStatusSchema,
  path: missionRoutePathSchema.nullable(),
  /** Point de départ et de retour (réglages de la carte); `null` : non défini. */
  depot: depotSchema.nullable(),
  nextRoute: missionNextRouteSchema.nullable(),
})
export type MissionRouteGuide = z.infer<typeof missionRouteGuideSchema>

/** Liaisons préparées par la tablette à partir du guide (le tracé est demandé au moment de s'en servir). */
export type RouteLiaisonKind = 'DEPOT_TO_ROUTE' | 'ROUTE_TO_ROUTE' | 'ROUTE_TO_DEPOT'

// ------------------------------------------------------------------ Cohérence (éditeur et serveur)

/** Écart toléré entre l'extrémité d'une étape et son arrêt : le fournisseur raccroche le point à la rue. */
export const ROUTE_PATH_SNAP_TOLERANCE_M = 250 as const

export type RoutePathIssue =
  | 'DUPLICATE_STOP'
  | 'ORDER_NOT_INCREASING'
  | 'LEG_COUNT'
  | 'LEG_MISMATCH'
  | 'LEG_ENDPOINT_FAR'
  | 'WAYPOINT_UNKNOWN_STOP'
  | 'TOO_MANY_VERTICES'

function metersBetween(a: LatLngTuple, b: LatLngTuple): number {
  const radius = 6_371_000
  const toRad = Math.PI / 180
  const dLat = (b[0] - a[0]) * toRad
  const dLng = (b[1] - a[1]) * toRad
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * toRad) * Math.cos(b[0] * toRad) * Math.sin(dLng / 2) ** 2
  return 2 * radius * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * Vérifie qu'un parcours est complet et décrit bien les résidences dans l'ordre : une étape par paire de résidences
 * consécutives, extrémités proches des arrêts, points de passage rattachés à un intervalle existant. Le serveur
 * applique les mêmes règles (`save_route_path_v1`). Liste vide : parcours cohérent.
 */
export function routePathIssues(
  stops: readonly RoutePathStop[],
  waypoints: readonly RoutePathWaypoint[],
  legs: readonly RoutePathLeg[],
): RoutePathIssue[] {
  const issues = new Set<RoutePathIssue>()
  const ids = new Set(stops.map((stop) => stop.serviceLocationId))
  if (ids.size !== stops.length) issues.add('DUPLICATE_STOP')
  for (let index = 1; index < stops.length; index += 1) {
    if ((stops[index]?.order ?? 0) <= (stops[index - 1]?.order ?? 0)) issues.add('ORDER_NOT_INCREASING')
  }
  if (legs.length !== stops.length - 1) issues.add('LEG_COUNT')
  legs.forEach((leg, index) => {
    const from = stops[index]
    const to = stops[index + 1]
    if (
      !from ||
      !to ||
      leg.fromServiceLocationId !== from.serviceLocationId ||
      leg.toServiceLocationId !== to.serviceLocationId
    ) {
      issues.add('LEG_MISMATCH')
      return
    }
    const first = leg.geometry[0]
    const last = leg.geometry[leg.geometry.length - 1]
    if (
      !first ||
      !last ||
      metersBetween(first, from.point) > ROUTE_PATH_SNAP_TOLERANCE_M ||
      metersBetween(last, to.point) > ROUTE_PATH_SNAP_TOLERANCE_M
    ) {
      issues.add('LEG_ENDPOINT_FAR')
    }
  })
  const lastId = stops[stops.length - 1]?.serviceLocationId
  for (const waypoint of waypoints) {
    if (!ids.has(waypoint.afterServiceLocationId) || waypoint.afterServiceLocationId === lastId) {
      issues.add('WAYPOINT_UNKNOWN_STOP')
    }
  }
  if (legs.reduce((sum, leg) => sum + leg.geometry.length, 0) > ROUTE_PATH_MAX_VERTICES)
    issues.add('TOO_MANY_VERTICES')
  return [...issues]
}
